import { View } from "@/components/ui";
import { WhatsappSupportBanner } from "@/components/shared/whatsapp-support-banner";
import { onChatSales } from "@/lib/chat-sales";
import { BookingOrderCard } from "@/components/shared/booking/order-card";
import { BookingNavigationBottom } from "@/components/shared/booking/order-card/BookingNavigationBottom";
import { AgreeTerms } from "../agree-terms";
import { getPlatform } from "@/lib/utils";
import { useDeletePackageStore } from "@/store/report-issue/package-issue/useDeletePackage";
import { useClientConfirmReservationStore } from "@/store/booking/clientConfirmReservation";
import type { ClientConfirmReservationInput } from "@/__generated__/graphql";
import {
  PackageDepartmentEnum,
  UserPlatformEnum,
} from "@/__generated__/graphql";
import { getUserDevice } from "@/lib/platform";
import { useBookingState } from "@/store/booking/useBookingState";
import { useBookingRoute } from "@/components/hooks/use-booking-route";
import {
  sendAnalyticData,
  transformReservationDataToPurchase,
} from "@/lib/monitoring/analytics";
import { showToast } from "@/components/ui/toast/show-toast";
import { getSalesPersonData } from "@/lib/get-sales-person-data";
import { useUnfinishedBookingState } from "@/store/booking/useUnfinishedBookingState";
import { getErrorMessage } from "@/lib/helpers/string";
import type { ServiceName } from "@/types/service";
import { generateBookingChatSalesMessage } from "@/lib/chat-sales";

export const BookingConfirmationNavigation = ({
  name,
}: {
  name: ServiceName;
}) => {
  const { forward } = useBookingRoute();
  const platform = getPlatform();
  const {
    data: {
      bookingInfo,
      promo,
      reservations,
      selectedAddress,
      packageDetailsFilter,
      reservationTimeStamp,
      isRebook,
      selectedServiceProfileId,
      department,
    },
    setBookingState,
  } = useBookingState();
  const { fetch: confirmReservation, loading: confirmReservationLoading } =
    useClientConfirmReservationStore();
  const { fetch: deletePackage } = useDeletePackageStore();
  const cleanupFailedPackages = async (createdPackageIds: string[]) => {
    if (!createdPackageIds.length) return;
    await Promise.all(
      createdPackageIds.map((pkgId) =>
        deletePackage({ requestPayload: { input: { id: pkgId } } }).catch(
          (err) => {
            const errMsg = getErrorMessage(
              err,
              "Error clean up failed package",
            );
            showToast({
              title: errMsg,
              type: "error",
            });
          },
        ),
      ),
    );
  };

  const handleReservationConfirmation = (
    result: Awaited<ReturnType<typeof confirmReservation>>,
    isLastReservation: boolean = true,
    createdPackageIds: string[] = [],
  ) => {
    if (result.error || !result.data?.clientConfirmReservation) {
      throw new Error("Failed to create reservation");
    }
    const confirmed = result.data.clientConfirmReservation;
    if (confirmed.package?.id) {
      createdPackageIds.push(confirmed.package.id);
    }
    if (isLastReservation) {
      const multiplier = !packageDetailsFilter?.repeatEveryTimes
        ? 1
        : packageDetailsFilter?.repeatEveryTimes;
      useUnfinishedBookingState.getState().resetUnfinishedBooking();
      sendAnalyticData({
        name: "purchase",
        values: transformReservationDataToPurchase(multiplier, confirmed),
      });
      if (isRebook) {
        sendAnalyticData({
          name: "rebook_complete",
          values: transformReservationDataToPurchase(multiplier, confirmed),
        });
      }
      setBookingState({
        completedBooking: true,
      });
      forward();
    }
  };

  const onContinue = async () => {
    const now = new Date().getTime();
    if (now > reservationTimeStamp) {
      showToast({
        title: "Your selected slot already expired, please select slot again",
        type: "error",
      });
      return;
    }
    if (!selectedAddress || !reservations.length || !bookingInfo) return;
    const input: ClientConfirmReservationInput = {
      accessType: `${bookingInfo.accessMode}: ${bookingInfo.accessInstructions}`,
      additionalNotes: bookingInfo.note,
      fullAddress: selectedAddress.fullAddress as string,
      postalCode: selectedAddress.postalCode,
      promoCode: promo?.appliedPromoCode,
      contactId: bookingInfo.contactId,
      reservationId: reservations[0].id,
      salespersonShortcode: getSalesPersonData(),
      unitNumber: selectedAddress.unitNumber,
      workerChosenByClient: false,
      creationSource: {
        device: getUserDevice(),
        platform:
          platform === "native"
            ? UserPlatformEnum.ClientApp
            : UserPlatformEnum.ClientWeb,
      },
    };
    if (selectedServiceProfileId) {
      input.clientServiceProfileId = selectedServiceProfileId;
    }
    // Deep Cleaning Confirm Reservation
    if (department === PackageDepartmentEnum.DeepCleaning) {
      const createdPackageIds: string[] = [];
      for (let i = 0; i < reservations.length; i++) {
        const reservationItem = reservations[i];
        const isLastDeepCleaningReservation = i === reservations.length - 1;
        const deepCleaningInput: ClientConfirmReservationInput = {
          ...input,
          reservationId: reservationItem.id,
          skipBookingMessage: !isLastDeepCleaningReservation,
          skipJobAssignment: !isLastDeepCleaningReservation,
          promoCode: i > 0 ? null : input.promoCode, // Remove Promo Code after first package
        };

        try {
          const result = await confirmReservation({
            requestPayload: { input: deepCleaningInput },
          });
          handleReservationConfirmation(
            result,
            isLastDeepCleaningReservation,
            createdPackageIds,
          );
        } catch (error) {
          cleanupFailedPackages(createdPackageIds);
          showToast({
            title:
              (error as Error)?.message ||
              "Failed to confirm reservation. Please try again.",
            type: "error",
          });
          break; // Stop processing further reservations on error
        }
      }
      return;
    }
    // Default confirm reservation
    try {
      const result = await confirmReservation({
        requestPayload: { input },
      });
      handleReservationConfirmation(result);
    } catch (error) {
      showToast({
        title:
          (error as Error)?.message ||
          "Failed to confirm reservation. Please try again.",
        type: "error",
      });
      // Error handling is done inside handleReservationConfirmation
    }
  };

  return (
    <View>
      {/* large screen */}
      <View className="hidden flex-col gap-4 md:flex">
        <BookingOrderCard
          loading={confirmReservationLoading}
          onContinue={onContinue}
          buttonText="Complete Booking"
          icon="shieldCheck"
          iconAlignment="start"
          extraInfo={
            <View className="border-t py-3">
              <AgreeTerms />
            </View>
          }
        />
        <WhatsappSupportBanner
          onClick={() =>
            onChatSales(true, generateBookingChatSalesMessage(name))
          }
        />
      </View>

      {/* narrow screen */}
      <BookingNavigationBottom
        name={name}
        loading={confirmReservationLoading}
        buttonText="Complete Booking"
        onContinue={onContinue}
        icon="shieldCheck"
        iconAlignment="start"
      />
    </View>
  );
};
